import __var_0__ from '@stdlib/number/float64/base/to-float32';
import __var_1__ from '@stdlib/math/base/assert/is-nanf';
import __var_2__ from '@stdlib/math/base/special/abs';
/**

*
* Copyright (c) 2020 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //
var float64ToFloat32 = __var_0__;
var isnanf = __var_1__;
var abs = __var_2__;


// MAIN //

/**
* Computes the sum of single-precision floating-point strided array elements, ignoring `NaN` values and using a second-order iterative Kahan–Babuška algorithm.
*
* ## Method
*
* -   This implementation uses a second-order iterative Kahan–Babuška algorithm, as described by Klein (2005).
*
* ## References
*
* -   Klein, Andreas. 2005. "A Generalized Kahan-Babuška-Summation-Algorithm." _Computing_ 76 (3): 279–93. doi:[10.1007/s00607-005-0139-x](https://doi.org/10.1007/s00607-005-0139-x).
*
* @param {PositiveInteger} N - number of indexed elements
* @param {Float32Array} x - input array
* @param {integer} stride - stride length
* @returns {number} sum
*
* @example
* var Float32Array = require( '@stdlib/array/float32' );
*
* var x = new Float32Array( [ 1.0, -2.0, NaN, 2.0 ] );
* var N = x.length;
*
* var v = snansumkbn2( N, x, 1 );
* // returns 1.0
*/
function snansumkbn2( N, x, stride ) {
	var sum;
	var ccs;
	var ix;
	var cs;
	var cc;
	var v;
	var t;
	var c;
	var i;

	if ( N <= 0 ) {
		return 0.0;
	}
	if ( N === 1 || stride === 0 ) {
		if ( isnanf( x[ 0 ] ) ) {
			return 0.0;
		}
		return x[ 0 ];
	}
	if ( stride < 0 ) {
		ix = (1-N) * stride;
	} else {
		ix = 0;
	}
	sum = 0.0;
	ccs = 0.0; // second order correction term for lost low order bits
	cs = 0.0; // first order correction term for lost low order bits
	for ( i = 0; i < N; i++ ) {
		v = x[ ix ];
		if ( isnanf( v ) === false ) {
			t = float64ToFloat32( sum + v );
			if ( abs( sum ) >= abs( v ) ) {
				c = float64ToFloat32( float64ToFloat32( sum-t ) + v );
			} else {
				c = float64ToFloat32( float64ToFloat32( v-t ) + sum );
			}
			sum = t;
			t = float64ToFloat32( cs + c );
			if ( abs( cs ) >= abs( c ) ) {
				cc = float64ToFloat32( float64ToFloat32(cs-t) + c );
			} else {
				cc = float64ToFloat32( float64ToFloat32(c-t) + cs );
			}
			cs = t;
			ccs = float64ToFloat32( ccs + cc );
		}
		ix += stride;
	}
	return float64ToFloat32( sum + float64ToFloat32( cs + ccs ) );
}


// EXPORTS //
export default snansumkbn2;
